import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Access, AgsRoleToDmsProcessMap, Node, Process, ProcessPdmPrefix } from '@prisma/client';
import {
  Button,
  ButtonProps,
  Collapse,
  CollapseProps,
  Descriptions,
  Dropdown,
  ModalProps,
  Result,
  Space,
  Spin,
  Typography,
} from 'antd';
import { DropdownButtonProps } from 'antd/es/dropdown';
import Table, { ColumnsType } from 'antd/es/table';

import { Role } from '@app/domain-interfaces';
import { Auth } from '@app/web-components';

import { useDeleteProcess, useFindUniqueProcess, useGetProcess } from '../data';

export const useProcessActions = () => {
  const [detailsId, setDetailsId] = useState<number | undefined>();
  const [deleteId, setDeleteId] = useState<number | undefined>();
  const deleter = useDeleteProcess();
  const navigate = useNavigate();
  const { user } = Auth.useUserAuth();

  const detailsProcess = useGetProcess(detailsId ?? 0, { enabled: !!detailsId });

  const deleteProcess = useFindUniqueProcess<Process & { t3Node?: Node; pdmPrefixes: ProcessPdmPrefix[] }>(
    { where: { id: deleteId }, include: { t3Node: true, pdmPrefixes: true } },
    { enabled: !!deleteId },
  );

  const _getAliasDescriptionItems = (data?: Process & { t3Node?: Node; pdmPrefixes: ProcessPdmPrefix[] }) => {
    const items = [
      { key: 'DMS', children: data?.name, label: 'Process' },
      {
        key: 'PDM',
        children: data?.pdmPrefixes.map((x) => x.prefix).join(', ') ?? '',
        label: `PDM Prefix${(data?.pdmPrefixes.length ?? 0) > 1 ? 'es' : ''}`,
      },
    ];

    if (data?.t3Node) {
      items.push({
        key: 'T3 Node',
        children: data.t3Node.name,
        label: 'T3 Node',
      });
    }

    return items;
  };

  const getActionButton = (record: { id: number }, props?: ButtonProps | DropdownButtonProps) =>
    user?.roles[Role.RULE_ADMIN] ? (
      <Dropdown.Button
        size="small"
        {...props}
        menu={{
          items: [
            { label: 'Edit', key: 'edit', icon: <EditOutlined /> },
            { label: 'Delete', key: 'delete', icon: <DeleteOutlined />, danger: true },
          ],
          onClick: (e) => {
            switch (e.key) {
              case 'edit':
                navigate(`./${record.id}`);
                break;
              case 'delete':
                setDeleteId(record.id);
                break;
              default:
                console.log(`Cannot handle key: ${e.key}.`);
                break;
            }
          },
        }}
        onClick={() => setDetailsId(record.id)}
      >
        <Space>
          <EyeOutlined />
          Details
        </Space>
      </Dropdown.Button>
    ) : (
      <Button icon={<EyeOutlined />} size="small" {...(props as ButtonProps)} onClick={() => setDetailsId(record.id)}>
        Details
      </Button>
    );

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const onCancel = () => {
      setDeleteId(undefined);
      deleter.reset();
    };

    const onOk = () => {
      if (deleter.isIdle || deleter.isError) {
        deleteId && deleter.mutate(deleteId);
      } else {
        onCancel();
      }
    };

    return {
      centered: true,
      title: 'Are you sure you want to delete this process?',
      ...props,
      children: deleter.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : deleter.isSuccess ? (
        <Result
          status="success"
          title="Successfully deleted process"
          subTitle={`Process ${deleteProcess.data?.name} has been deleted`}
        />
      ) : deleter.isError ? (
        <Result status="error" title="Error occured when deleting process" subTitle={deleter.error.message} />
      ) : (
        <Descriptions
          bordered
          size="small"
          layout="horizontal"
          column={1}
          items={_getAliasDescriptionItems(deleteProcess.data)}
        />
      ),
      cancelButtonProps: { style: deleter.isSuccess ? { display: 'none' } : undefined, disabled: deleter.isPending },
      okButtonProps: {
        icon: deleter.isSuccess ? <CloseCircleOutlined /> : <DeleteOutlined />,
        disabled: deleter.isPending,
      },
      okText: deleter.isIdle || deleter.isPending ? 'Delete' : deleter.isError ? 'Retry' : 'Close',
      okType: deleter.isSuccess ? undefined : 'danger',
      open: !!deleteId,
      width: 800,
      onCancel,
      onOk,
    };
  };

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    const footer = [
      ...(user?.roles[Role.RULE_ADMIN]
        ? [
            <Button icon={<DeleteOutlined />} key="delete" onClick={() => setDeleteId(detailsProcess.data?.id)}>
              Delete
            </Button>,
            <Button icon={<EditOutlined />} key="edit" onClick={() => detailsId && navigate(`./${detailsId}`)}>
              Edit
            </Button>,
          ]
        : []),
      <Button key="close" onClick={() => setDetailsId(undefined)} icon={<CloseCircleOutlined />} type="primary">
        Close
      </Button>,
    ];

    const agsRolesColumns: ColumnsType<AgsRoleToDmsProcessMap & { access: Access }> = [
      { title: 'Name', dataIndex: ['access', 'name'] },
      { title: 'Employee Type', dataIndex: ['employeeType'] },
    ];

    const ipItems: CollapseProps['items'] = detailsProcess.data?.pdmPrefixes.map((x) => ({
      key: x.id,
      label: x.prefix,
      children: (
        <Table
          rowKey="id"
          columns={[
            { title: 'Type', dataIndex: ['type'] },
            { title: 'Pattern', dataIndex: ['pattern'] },
            {
              title: 'Optional',
              dataIndex: ['isOptional'],
              render: (value: boolean) => (value ? <CheckOutlined /> : undefined),
            },
          ]}
          dataSource={x.thirdPartyIp}
        />
      ),
    }));

    const items = _getAliasDescriptionItems(detailsProcess.data);

    return {
      centered: true,
      title: `${detailsProcess.data?.name} Details`,
      width: 1100,
      ...props,
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Descriptions bordered size="small" layout="horizontal" column={1} items={items} />
          <div>
            <Typography.Title level={5}>Third Party IP</Typography.Title>
            <Collapse items={ipItems} />
          </div>
          <Table
            rowKey="id"
            caption={
              <Typography.Title level={5} style={{ float: 'left' }}>
                AGS Role Mappings
              </Typography.Title>
            }
            columns={agsRolesColumns}
            dataSource={detailsProcess.data?.agsRoleToDmsProcessMap}
            loading={detailsProcess.isPending}
            pagination={false}
            size="small"
          />
        </Space>
      ),
      footer,
      open: !!detailsId,
      onCancel: () => setDetailsId(undefined),
    };
  };

  return { getActionButton, getDeleteModalProps, getDetailsModalProps };
};
